import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { tintOnWhite } from '../../theme/colorUtils';
import { radii, s } from '../../theme/tokens';
import {
  chartStatusDarkenCoefficient,
  lightModeChartStatusColors,
  uiTokens,
} from '../../theme/uiTokens';
import { chartBarSizing } from '../theme/chartBarSizing';
import {
  calculateGuidedYAxisMax,
  calculateGuidedYAxisTicks,
  guidedYAxisTickNumber,
} from './barChartAxisScale';
import { BreakdownTooltip, type BarTooltipBreakdownByLabel } from './BarChartBreakdownTooltip';
import { BarChartPlotBorders } from './BarChartPlotBorders';
import { BarChartRoundedTopBar } from './BarChartRoundedTopBar';
import { Box } from './Box';
import { formatChartDateLabel, formatChartTooltipDateLabel } from './chartDateLabel';
import { resolveChartPlotAreaBackgroundColor } from './chartPlotAreaBackgroundColor';
import { ChartSwatch } from './ChartSwatch';
import { buildAdaptiveTickLabelInterval, buildTickLabelInterval } from './chartTickLabelInterval';
import { getChartTooltipSx } from './chartTooltipStyles';
import {
  chartAxisLabelFontSize,
  chartAxisLabelOpacity,
  chartAxisLineOpacity,
  chartGridLineOpacity,
  chartLegendFontSizePx,
  chartLegendLineHeight,
} from './chartVisualTokens';
import { darken } from './colorUtils';
import { useTheme } from './hooks';
import { Text } from './Text';
import {
  buildTooltipOverlaySurfaceSx,
  tooltipOverlayBodyFontWeight,
  tooltipOverlayCompactLineHeight,
  tooltipOverlayFontSize,
  tooltipOverlayLabelFontWeight,
  tooltipOverlayMetricFontWeight,
  tooltipOverlayPaddingX,
  tooltipOverlayPaddingY,
  tooltipOverlayTextColor,
  tooltipOverlayTitleFontSize,
} from './tooltipOverlayStyles';
import { useBarChartPlotAreaBottomInset } from './useBarChartPlotAreaBottomInset';
import { useRecentChartItemSelection } from './useRecentChartItemSelection';
import { MuiBarChart } from './xCharts';
import type { SxProps, Theme } from '@mui/material/styles';
import type { ChartsAxisData } from '@mui/x-charts/models';
import type { MouseEvent as ReactMouseEvent } from 'react';

export type BarChartDatum = Record<string, string | number | null>;

type BarSeries = { key: string; label: string; color?: string };
const chartStatusColors = new Set(Object.values(uiTokens.statusColors));

type BarChartProps = {
  data: BarChartDatum[];
  series: BarSeries[];
  height?: number;
  marginLeft?: number;
  marginBottom?: number;
  xAxisHeight?: number;
  yAxisWidth?: number;
  showLegend?: boolean;
  hideYAxis?: boolean;
  showPlotAreaBackground?: boolean;
  hideXAxisLine?: boolean;
  showHorizontalGrid?: boolean;
  tooltipValueSuffixByLabel?: Readonly<Record<string, string>>;
  tooltipMultiline?: boolean;
  tooltipBreakdownByLabel?: BarTooltipBreakdownByLabel;
  xAxisLabelFormatter?: (label: string) => string;
  yAxisMin?: number;
  yAxisMax?: number;
  xTickEvery?: number;
  showAllXAxisLabels?: boolean;
  onSelectBar?: (dataIndex: number) => void;
  categoryGapRatio?: number;
  barGapRatio?: number;
  barCornerRadius?: number;
  skipAnimation?: boolean;
  disableInteraction?: boolean;
  disableTooltip?: boolean;
  preserveSeriesColors?: boolean;
  roundedTopBars?: boolean;
  useStackedXAxisPlacement?: boolean;
  showVerticalPlotBorders?: boolean;
  yReferenceLines?: Array<{ value: number; color: string; label?: string }>;
};

export const BarChart = ({
  data,
  series,
  height = 260,
  marginLeft = -8,
  marginBottom,
  xAxisHeight: xAxisHeightOverride,
  yAxisWidth = 72,
  showLegend = true,
  hideYAxis = false,
  showPlotAreaBackground = true,
  hideXAxisLine = true,
  showHorizontalGrid = true,
  tooltipValueSuffixByLabel,
  tooltipMultiline = false,
  tooltipBreakdownByLabel,
  xAxisLabelFormatter = formatChartDateLabel,
  yAxisMin,
  yAxisMax,
  xTickEvery,
  showAllXAxisLabels = false,
  onSelectBar,
  categoryGapRatio,
  barGapRatio,
  barCornerRadius = radii.sm,
  skipAnimation = false,
  disableInteraction = false,
  disableTooltip = false,
  preserveSeriesColors = false,
  roundedTopBars = false,
  useStackedXAxisPlacement = false,
  showVerticalPlotBorders = false,
  yReferenceLines = [],
}: BarChartProps): JSX.Element => {
  const theme = useTheme();
  const statusColors =
    theme.palette.mode === 'light' ? lightModeChartStatusColors : uiTokens.statusColors;
  const barChartPalette = useMemo(
    () => [
      statusColors.purple,
      statusColors.green,
      statusColors.orange,
      statusColors.blue,
      statusColors.red,
    ],
    [statusColors],
  );
  const containerRef = useRef<HTMLDivElement | null>(null);
  const hasRenderedNonEmptyDataRef = useRef(false);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const formatWholeNumber = useCallback((value: number | null): string => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return '';
    return Number.isInteger(value) ? String(value) : '';
  }, []);
  const formatTooltipValue = useCallback(
    (seriesKey: string, value: number | null): string => {
      if (typeof value !== 'number' || !Number.isFinite(value)) return '';
      if (seriesKey.toLowerCase() === 'passrate') {
        const rounded = value.toFixed(2).replace(/\.?0+$/, '');
        return `${rounded}%`;
      }
      return formatWholeNumber(value);
    },
    [formatWholeNumber],
  );
  const formatTooltipWithBreakdown = useCallback(
    (seriesKey: string, value: number | null, dataIndex?: number): string => {
      const base = formatTooltipValue(seriesKey, value);
      if (!tooltipValueSuffixByLabel || typeof dataIndex !== 'number' || dataIndex < 0) {
        return base;
      }
      const datum = data[dataIndex];
      const labelValue = datum.label;
      const labelKey = typeof labelValue === 'string' ? labelValue : String(labelValue);
      const suffix = tooltipValueSuffixByLabel[labelKey];
      if (!suffix) return base;
      return `${base} (${suffix})`;
    },
    [data, formatTooltipValue, tooltipValueSuffixByLabel],
  );
  const xAxisValues = useMemo(() => data.map((item) => String(item.label)), [data]);
  const xLabels = useMemo(
    () => xAxisValues.map((label) => xAxisLabelFormatter(label)),
    [xAxisLabelFormatter, xAxisValues],
  );
  const xTickStep =
    typeof xTickEvery === 'number' && Number.isFinite(xTickEvery) && xTickEvery > 1
      ? Math.floor(xTickEvery)
      : null;
  const estimatedXAxisWidth =
    typeof containerWidth === 'number'
      ? Math.max(0, containerWidth - yAxisWidth - 16 - Math.abs(marginLeft))
      : null;
  const xTickLabelInterval = showAllXAxisLabels
    ? buildAdaptiveTickLabelInterval(xLabels.length, true, estimatedXAxisWidth)
    : xTickStep === null
      ? buildTickLabelInterval(xLabels.length, estimatedXAxisWidth)
      : (() => {
          const visibleLabels = new Set(
            xAxisValues.filter((_, index) => {
              const isStepTick = index % xTickStep === 0;
              const isLast = index === xAxisValues.length - 1;
              return isStepTick || isLast;
            }),
          );
          return (value: string) => visibleLabels.has(value);
        })();
  const resolvedCategoryGapRatio = categoryGapRatio ?? chartBarSizing.default.categoryGapRatio;
  const resolvedBarGapRatio = barGapRatio ?? chartBarSizing.default.barGapRatio;
  const resolvedXAxisHeight = xAxisHeightOverride ?? 32;
  const resolvedMarginBottom =
    marginBottom ?? Math.max(24, resolvedXAxisHeight + (hideXAxisLine ? 8 : 10));
  const resolveBarColor = useCallback(
    (base: string): string =>
      preserveSeriesColors
        ? base
        : theme.palette.mode === 'dark'
          ? darken(base, chartStatusColors.has(base) ? chartStatusDarkenCoefficient : 0.26)
          : tintOnWhite(base, 0.7),
    [preserveSeriesColors, theme.palette.mode],
  );
  const resolveSeriesColor = useCallback(
    (item: BarSeries, index: number): string =>
      resolveBarColor(item.color ?? barChartPalette[index % barChartPalette.length]),
    [barChartPalette, resolveBarColor],
  );

  const hasLegendLabels = series.some((item) => item.label.trim().length > 0);
  const hideLegend = !showLegend || !hasLegendLabels;
  const isPassRateChart = series.some((item) => item.key.toLowerCase() === 'passrate');
  const dataMax = series.reduce((maxValue, item) => {
    const seriesMax = data.reduce((rowMax, row) => {
      const rawValue = row[item.key];
      const value = typeof rawValue === 'number' ? rawValue : null;
      return value !== null && Number.isFinite(value) && value > rowMax ? value : rowMax;
    }, 0);
    return seriesMax > maxValue ? seriesMax : maxValue;
  }, 0);
  const referenceMax = yReferenceLines.reduce((maxValue, line) => {
    if (!Number.isFinite(line.value)) return maxValue;
    return line.value > maxValue ? line.value : maxValue;
  }, 0);
  const computedYAxisMax = Math.max(dataMax, referenceMax);
  const effectiveSkipAnimation = skipAnimation || hasRenderedNonEmptyDataRef.current;
  const boundedYAxisMax = Math.max(yAxisMax ?? 0, computedYAxisMax);
  const shouldUseIntegerYAxisMax =
    !isPassRateChart &&
    data.every((row) =>
      series.every((item) => {
        const value = row[item.key];
        return typeof value !== 'number' || Number.isInteger(value);
      }),
    ) &&
    yReferenceLines.every((line) => Number.isInteger(line.value));
  const resolvedYAxisMin = yAxisMin ?? 0;
  const resolvedYAxisMax = isPassRateChart
    ? 100
    : calculateGuidedYAxisMax(boundedYAxisMax, shouldUseIntegerYAxisMax);
  const yAxisTicks = calculateGuidedYAxisTicks(
    resolvedYAxisMin,
    resolvedYAxisMax,
    shouldUseIntegerYAxisMax,
  );
  const formatGuidedYAxisTick = useCallback(
    (value: number | null): string => formatWholeNumber(value),
    [formatWholeNumber],
  );
  const { markRecentItemSelection, shouldSuppressAxisSelection } = useRecentChartItemSelection();
  const finiteReferenceLines = yReferenceLines.filter((line) => Number.isFinite(line.value));
  const tooltipTrigger = disableTooltip ? 'none' : undefined;
  const tooltipSxParts: Array<
    Record<string, unknown> | ((theme: Theme) => Record<string, unknown>)
  > = [getChartTooltipSx(theme) as Record<string, unknown>];
  if (tooltipMultiline) {
    tooltipSxParts.push({
      '& .MuiChartsTooltip-cell': {
        whiteSpace: 'pre-line',
        verticalAlign: 'top',
      },
      '& .MuiChartsTooltip-valueCell': {
        whiteSpace: 'pre-line',
      },
    });
  }
  if (tooltipBreakdownByLabel) {
    tooltipSxParts.push({
      '& .flowqa-bar-tooltip-paper': {
        ...buildTooltipOverlaySurfaceSx(theme),
        maxWidth: 'min(calc(100vw - 32px), 720px)',
        width: 'max-content',
        paddingTop: `${tooltipOverlayPaddingY} !important`,
        paddingBottom: `${tooltipOverlayPaddingY} !important`,
        paddingLeft: '0 !important',
        paddingRight: '0 !important',
      },
      '& .flowqa-bar-tooltip-table': {
        borderCollapse: 'separate',
        borderSpacing: 0,
        width: '100% !important',
        tableLayout: 'auto',
      },
      '& .flowqa-bar-tooltip-table > thead > tr > th, & .flowqa-bar-tooltip-table > thead > tr > td':
        {
          borderBottom: '0 !important',
          boxShadow: 'none !important',
          paddingLeft: `${tooltipOverlayPaddingX} !important`,
          paddingRight: `${tooltipOverlayPaddingX} !important`,
          paddingTop: '0 !important',
          paddingBottom: `${tooltipOverlayPaddingY} !important`,
          verticalAlign: 'middle !important',
          textAlign: 'left !important',
          color: `${tooltipOverlayTextColor} !important`,
          fontSize: `${tooltipOverlayTitleFontSize} !important`,
          fontWeight: `${tooltipOverlayLabelFontWeight} !important`,
        },
      '& .flowqa-bar-tooltip-table > thead > tr > th': {
        paddingRight: `${s('2xl')} !important`,
        textAlign: 'left !important',
      },
      '& .flowqa-bar-tooltip-table > thead > tr > td': {
        textAlign: 'right !important',
        whiteSpace: 'nowrap',
      },
      '& .flowqa-bar-tooltip-divider-row': {
        height: '1px !important',
      },
      '& .flowqa-bar-tooltip-divider-cell': {
        backgroundColor: 'transparent !important',
        backgroundImage: `linear-gradient(${theme.app.colors.border}, ${theme.app.colors.border}) !important`,
        backgroundPosition: 'center top !important',
        backgroundRepeat: 'no-repeat !important',
        backgroundSize: `calc(100% - (${tooltipOverlayPaddingX} * 2)) 1px !important`,
        borderBottom: '0 !important',
        borderTop: '0 !important',
        fontSize: '0 !important',
        height: '0 !important',
        lineHeight: '0 !important',
        padding: '0 !important',
      },
      '& .flowqa-bar-tooltip-table > tbody > tr > th, & .flowqa-bar-tooltip-table > tbody > tr > td':
        {
          paddingTop: '3px !important',
          paddingBottom: '3px !important',
          paddingLeft: `${tooltipOverlayPaddingX} !important`,
          paddingRight: `${tooltipOverlayPaddingX} !important`,
          verticalAlign: 'middle !important',
          lineHeight: `${String(tooltipOverlayCompactLineHeight)} !important`,
        },
      '& .flowqa-bar-tooltip-table > tbody > tr > th': {
        textAlign: 'left !important',
        color: `${tooltipOverlayTextColor} !important`,
        whiteSpace: 'nowrap',
      },
      '& .flowqa-bar-tooltip-table > tbody > tr > td': {
        textAlign: 'right !important',
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',
        fontWeight: `${tooltipOverlayMetricFontWeight} !important`,
      },
      '& .flowqa-bar-tooltip-table > tbody > tr:first-of-type > th, & .flowqa-bar-tooltip-table > tbody > tr:first-of-type > td':
        {
          paddingTop: '8px !important',
        },
      '& .flowqa-bar-tooltip-table > tbody > tr:last-of-type > th, & .flowqa-bar-tooltip-table > tbody > tr:last-of-type > td':
        {
          paddingBottom: '4px !important',
        },
      '& .flowqa-bar-tooltip-cell, & .flowqa-bar-tooltip-table th, & .flowqa-bar-tooltip-table td':
        {
          color: tooltipOverlayTextColor,
          fontSize: tooltipOverlayFontSize,
          lineHeight: tooltipOverlayCompactLineHeight,
          fontWeight: tooltipOverlayBodyFontWeight,
        },
      '& .flowqa-bar-tooltip-table tr': {
        lineHeight: `${String(tooltipOverlayCompactLineHeight)} !important`,
      },
    });
  }
  const tooltipSx = tooltipSxParts as SxProps<Theme>;
  const chartMargin = hideYAxis
    ? { left: 16, right: 16, top: 16, bottom: resolvedMarginBottom }
    : { left: marginLeft, right: 16, top: 16, bottom: resolvedMarginBottom };
  const plotAreaBottomInset = useBarChartPlotAreaBottomInset({
    chartMarginBottom: chartMargin.bottom,
    containerRef,
    data,
    height,
    series,
  });
  const plotAreaLeft = hideYAxis ? chartMargin.left : chartMargin.left + yAxisWidth;
  const visibleYAxisLineLeft = Math.max(0, plotAreaLeft - 1);
  const plotAreaBackgroundColor = resolveChartPlotAreaBackgroundColor(theme);
  const hideLightModePlotAreaTopBorder = theme.palette.mode === 'light';
  const hiddenYAxisConfig = hideYAxis
    ? {
        position: 'none' as const,
        width: 0,
        disableLine: true,
        disableTicks: true,
        tickLabelInterval: () => false,
        tickSize: 0,
        tickNumber: 0,
        tickSpacing: 9999,
        tickLabelStyle: { display: 'none', fontSize: 0 },
        labelStyle: { display: 'none', fontSize: 0 },
      }
    : null;

  const handleItemClick = useCallback(
    (_event: ReactMouseEvent<SVGElement>, item: { dataIndex?: number }) => {
      if (!onSelectBar) return;
      const dataIndex = item.dataIndex;
      if (typeof dataIndex !== 'number' || !Number.isFinite(dataIndex)) return;
      markRecentItemSelection(dataIndex);
      onSelectBar(dataIndex);
    },
    [markRecentItemSelection, onSelectBar],
  );
  const handleAxisClick = useCallback(
    (_event: MouseEvent, axisData: ChartsAxisData | null) => {
      if (!onSelectBar) return;
      const target = _event.target;
      if (target && target instanceof Element && target.closest('.MuiBarElement-root')) {
        return;
      }
      const dataIndex = axisData?.dataIndex;
      if (typeof dataIndex !== 'number' || !Number.isFinite(dataIndex)) return;
      if (shouldSuppressAxisSelection(dataIndex)) {
        return;
      }
      onSelectBar(dataIndex);
    },
    [onSelectBar, shouldSuppressAxisSelection],
  );
  const chartSeries = useMemo(
    () =>
      series.map((item, index) => ({
        id: item.key,
        data: data.map((row) => {
          const value = row[item.key];
          return typeof value === 'number' && Number.isFinite(value) ? value : null;
        }),
        label: item.label,
        color: resolveSeriesColor(item, index),
        valueFormatter: (value: number | null, context: { dataIndex?: number }) => {
          const maybeDataIndex =
            typeof context.dataIndex === 'number' ? context.dataIndex : undefined;
          return formatTooltipWithBreakdown(item.key, value, maybeDataIndex);
        },
      })),
    [data, formatTooltipWithBreakdown, resolveSeriesColor, series],
  );
  const topSeriesKeyByRow = useMemo(
    () => data.map(() => series[series.length - 1]?.key ?? null),
    [data, series],
  );
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    const updateWidth = (): void => {
      const nextWidth = container.clientWidth;
      setContainerWidth((currentWidth) => (currentWidth === nextWidth ? currentWidth : nextWidth));
    };
    updateWidth();
    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });
    resizeObserver.observe(container);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  useEffect(() => {
    hasRenderedNonEmptyDataRef.current ||= data.length > 0 && series.length > 0 && dataMax > 0;
  }, [data.length, dataMax, series.length]);
  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height,
        position: 'relative',
        overflow: 'visible',
        backgroundColor: 'transparent',
        color: theme.app.colors.divider,
        '& .MuiChartsSurface-root': {
          backgroundColor: 'transparent !important',
          color: theme.app.colors.divider,
          overflow: 'visible',
        },
        '& .MuiChartsSurface-root svg': {
          backgroundColor: 'transparent !important',
          overflow: 'visible',
        },
        '& .MuiChartsLegend-root': { display: 'none' },
      }}
    >
      {showPlotAreaBackground ? (
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            left: Math.max(0, plotAreaLeft),
            right: chartMargin.right,
            top: chartMargin.top,
            bottom: plotAreaBottomInset ?? chartMargin.bottom,
            backgroundColor: plotAreaBackgroundColor,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
      ) : null}
      {!hideYAxis ? (
        <Box
          data-chart-axis="y"
          sx={{
            position: 'absolute',
            left: visibleYAxisLineLeft,
            top: chartMargin.top,
            bottom: plotAreaBottomInset ?? chartMargin.bottom,
            width: '1px',
            backgroundColor: theme.app.colors.divider,
            pointerEvents: 'none',
            zIndex: 2,
          }}
          aria-hidden="true"
        />
      ) : null}
      <BarChartPlotBorders
        bottom={plotAreaBottomInset ?? chartMargin.bottom}
        color={theme.app.colors.divider}
        hideTopBorder={hideLightModePlotAreaTopBorder}
        left={plotAreaLeft}
        right={chartMargin.right}
        showVerticalBorders={showVerticalPlotBorders}
        top={chartMargin.top}
      />
      <MuiBarChart
        height={height}
        skipAnimation={effectiveSkipAnimation}
        slots={{
          ...(roundedTopBars
            ? {
                bar: (barProps) => (
                  <BarChartRoundedTopBar
                    {...barProps}
                    topSeriesKeyByRow={topSeriesKeyByRow}
                    barRadius={barCornerRadius}
                  />
                ),
              }
            : {}),
          ...(tooltipBreakdownByLabel
            ? {
                tooltip: (tooltipProps) => {
                  const entries = Object.entries(tooltipBreakdownByLabel);
                  const breakdownByFormattedLabel = Object.fromEntries(
                    entries.flatMap(([label, breakdown]) => [
                      [xAxisLabelFormatter(label), breakdown],
                      [formatChartTooltipDateLabel(label), breakdown],
                    ]),
                  );
                  return (
                    <BreakdownTooltip
                      {...tooltipProps}
                      breakdownByLabel={breakdownByFormattedLabel}
                    />
                  );
                },
              }
            : {}),
        }}
        borderRadius={barCornerRadius}
        onItemClick={onSelectBar && !disableInteraction ? handleItemClick : undefined}
        onAxisClick={onSelectBar && !disableInteraction ? handleAxisClick : undefined}
        series={chartSeries}
        xAxis={[
          {
            data: xAxisValues,
            scaleType: 'band',
            height: resolvedXAxisHeight,
            categoryGapRatio: resolvedCategoryGapRatio,
            barGapRatio: resolvedBarGapRatio,
            ...(useStackedXAxisPlacement
              ? { tickPlacement: 'extremities' as const, tickLabelPlacement: 'middle' as const }
              : {}),
            valueFormatter: (value, context) =>
              context.location === 'tooltip'
                ? formatChartTooltipDateLabel(String(value))
                : xAxisLabelFormatter(String(value)),
            tickLabelInterval: xTickLabelInterval,
            tickInterval: xAxisValues,
            tickLabelStyle: {
              fill: theme.app.colors.textMuted,
              fontSize: chartAxisLabelFontSize,
              opacity: chartAxisLabelOpacity,
              transform: 'translateY(6px)',
            },
            tickSize: 6,
          },
        ]}
        yAxis={[
          {
            min: resolvedYAxisMin,
            max: resolvedYAxisMax,
            width: hideYAxis ? 0 : yAxisWidth,
            tickLabelStyle: hideYAxis
              ? { display: 'none', fontSize: 0 }
              : {
                  fill: theme.app.colors.textMuted,
                  fontSize: chartAxisLabelFontSize,
                  opacity: chartAxisLabelOpacity,
                },
            tickSize: 0,
            tickInterval: yAxisTicks,
            tickNumber: guidedYAxisTickNumber,
            valueFormatter: hideYAxis ? () => '' : formatGuidedYAxisTick,
            ...(hiddenYAxisConfig ?? {}),
          },
        ]}
        grid={{ horizontal: showHorizontalGrid, vertical: false }}
        margin={chartMargin}
        slotProps={{ tooltip: { sx: tooltipSx, trigger: tooltipTrigger } }}
        sx={{
          position: 'relative',
          zIndex: 1,
          '& .MuiChartsAxis-tickLabel': {
            fill: theme.app.colors.textMuted,
            opacity: chartAxisLabelOpacity,
          },
          '& .MuiChartsAxis-directionY .MuiChartsAxis-tickLabel': {
            transform: 'translateX(-6px)',
          },
          '& .MuiChartsAxis-directionY .MuiChartsAxis-line, & .MuiChartsAxis-directionY .MuiChartsAxis-line[stroke]':
            {
              stroke: 'transparent !important',
            },
          '& .MuiChartsAxis-directionX .MuiChartsAxis-line, & .MuiChartsAxis-directionX .MuiChartsAxis-line[stroke]':
            {
              stroke: `${theme.app.colors.divider} !important`,
              strokeOpacity: chartAxisLineOpacity,
            },
          '& .MuiChartsAxis-directionX .MuiChartsAxis-tick, & .MuiChartsAxis-directionX .MuiChartsAxis-tick[stroke]':
            {
              stroke: `${theme.app.colors.divider} !important`,
              strokeOpacity: chartAxisLineOpacity,
            },
          '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': {
            stroke: theme.app.colors.divider,
            strokeOpacity: chartAxisLineOpacity,
          },
          '& .MuiChartsGrid-horizontalLine': {
            stroke: theme.app.colors.divider,
            strokeOpacity: chartGridLineOpacity,
          },
          '& .MuiChartsAxis-line[stroke]': {
            stroke: `${theme.app.colors.divider} !important`,
          },
          '& .MuiChartsAxis-tick[stroke]': {
            stroke: `${theme.app.colors.divider} !important`,
          },
          ...(hideXAxisLine
            ? {
                '& .MuiChartsAxis-directionX .MuiChartsAxis-line, & .MuiChartsAxis-directionX .MuiChartsAxis-line[stroke]':
                  {
                    stroke: 'transparent !important',
                  },
              }
            : null),
          ...(hideYAxis
            ? {
                '& .MuiChartsAxis-directionY .MuiChartsAxis-line, & .MuiChartsAxis-directionY .MuiChartsAxis-line[stroke]':
                  {
                    stroke: 'transparent !important',
                  },
                '& .MuiChartsAxis-directionY .MuiChartsAxis-tick, & .MuiChartsAxis-directionY .MuiChartsAxis-tick[stroke]':
                  {
                    stroke: 'transparent !important',
                  },
                '& .MuiChartsAxis-directionY .MuiChartsAxis-tickLabel': {
                  fill: 'transparent !important',
                },
              }
            : null),
          ...(onSelectBar && !disableInteraction
            ? {
                '& .MuiChartsAxis-directionX': { cursor: 'pointer' },
                '& .MuiChartsAxis-directionX .MuiChartsAxis-tickLabel': { cursor: 'pointer' },
                '& .MuiChartsAxis-directionX .MuiChartsAxis-tick': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight-root': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight-root *': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight *': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight-line, & .MuiChartsAxisHighlight-rect': {
                  cursor: 'pointer',
                },
                '& .MuiChartsAxisHighlightLine, & .MuiChartsAxisHighlightRect': {
                  cursor: 'pointer',
                },
                '& .MuiChartsOverlay-root': { cursor: 'pointer' },
                '& svg': { cursor: 'pointer' },
              }
            : null),
          ...(disableInteraction
            ? {
                '& .MuiChartsOverlay-root': { pointerEvents: 'none' },
                '& .MuiChartsAxisHighlight-root': { pointerEvents: 'none' },
                '& .MuiChartsAxisHighlight': { pointerEvents: 'none' },
                '& .MuiBarElement-root': { pointerEvents: 'none' },
              }
            : null),
        }}
      >
        {finiteReferenceLines.map((line, index) => (
          <ChartsReferenceLine
            key={`bar-guide-${line.value}-${line.color}-${index}`}
            y={line.value}
            lineStyle={{ stroke: line.color, strokeDasharray: '8 6', strokeWidth: 0.5 }}
          />
        ))}
      </MuiBarChart>
      {!hideLegend ? (
        <Box sx={{ display: 'grid', gap: s('xs'), marginTop: s('sm') }}>
          {series.map((item, index) => (
            <Box key={item.key} sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: s('xs') }}>
                <ChartSwatch color={resolveSeriesColor(item, index)} />
                <Text
                  role="body"
                  sx={{
                    fontSize: `${String(chartLegendFontSizePx)}px`,
                    lineHeight: chartLegendLineHeight,
                  }}
                >
                  {item.label}
                </Text>
              </Box>
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
};
